import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApps, initializeApp } from 'firebase/app';
import { firebaseConfig, getEnvironment, isDebugMode } from '../config/firebase';

interface AzureADConfig {
    clientId: string;
    authority: string;
    redirectUri: string;
    scopes: string[];
    tenantId: string;
}

interface AppConfig {
    name: string;
    version: string;
    environment: string;
}

interface SecureConfigResponse {
    success: boolean;
    message: string;
    config: {
        azure?: AzureADConfig;
        app?: AppConfig;
    };
    environment: string;
    timestamp: string;
}

// Secure Configuration Service
export class SecureConfigService {
    private static instance: SecureConfigService;
    private functions: any;
    private cachedConfigs: Map<string, any> = new Map();

    // Method to reset the singleton (useful for testing or env changes)
    public static resetInstance(): void {
        SecureConfigService.instance = null as any;
    }

    private constructor() {
        // Get or initialize Firebase app
        const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

        // Initialize Functions instance - always use production Functions for secure config and auth flows.
        // The Azure AD login and mapAzureADUser are hosted in the main Functions project, not the local
        // emulator on :5100, and routing through the emulator from the browser breaks CORS.
        this.functions = getFunctions(app, 'us-central1');
        console.log('[SecureConfigService] ✅ Using production Firebase Functions (emulator disabled for web)');
    }

    public static getInstance(): SecureConfigService {
        if (!SecureConfigService.instance) {
            SecureConfigService.instance = new SecureConfigService();
        }
        return SecureConfigService.instance;
    }

    // Get Azure AD configuration securely
    async getAzureADConfig(environment?: 'dev' | 'prod', organizationId?: string): Promise<AzureADConfig> {
        const detectedEnvironment = environment || getEnvironment();
        // Include organizationId in cache key to cache org-specific configs separately
        const cacheKey = `azure_${detectedEnvironment}_${organizationId || 'default'}`;

        if (this.cachedConfigs.has(cacheKey)) {
            return this.cachedConfigs.get(cacheKey);
        }

        try {
            console.log('[SecureConfigService] Getting Azure AD config with hostname:', window.location.hostname, organizationId ? `for org: ${organizationId}` : '');
            const getSecureConfig = httpsCallable(this.functions, 'getSecureConfig');
            const result = await getSecureConfig({
                configType: 'azure',
                environment: detectedEnvironment,
                hostname: window.location.hostname,
                organizationId: organizationId
            });

            const response = result.data as SecureConfigResponse;

            if (response.success && response.config.azure) {
                // Only log sensitive info in debug mode
                if (isDebugMode()) {
                    console.log('[SecureConfigService] Azure AD config received:', {
                        clientId: response.config.azure.clientId,
                        redirectUri: response.config.azure.redirectUri,
                        environment: detectedEnvironment,
                        hostname: window.location.hostname
                    });
                } else {
                    console.log('[SecureConfigService] Azure AD config received:', {
                        clientId: response.config.azure.clientId ? 'SET' : 'NOT SET',
                        redirectUri: response.config.azure.redirectUri ? 'SET' : 'NOT SET',
                        environment: detectedEnvironment,
                        hostname: window.location.hostname
                    });
                }
                this.cachedConfigs.set(cacheKey, response.config.azure);
                return response.config.azure;
            } else {
                throw new Error('Failed to retrieve Azure AD configuration');
            }
        } catch (error: any) {
            console.error('❌ Failed to get Azure AD config:', error);
            throw error;
        }
    }

    // Get app configuration securely
    async getAppConfig(environment?: 'dev' | 'prod'): Promise<AppConfig> {
        const detectedEnvironment = environment || getEnvironment();
        const cacheKey = `app_${detectedEnvironment}`;

        if (this.cachedConfigs.has(cacheKey)) {
            return this.cachedConfigs.get(cacheKey);
        }

        try {
            const getSecureConfig = httpsCallable(this.functions, 'getSecureConfig');
            const result = await getSecureConfig({
                configType: 'app',
                environment: detectedEnvironment,
                hostname: window.location.hostname
            });

            const response = result.data as SecureConfigResponse;

            if (response.success && response.config.app) {
                this.cachedConfigs.set(cacheKey, response.config.app);
                return response.config.app;
            } else {
                throw new Error('Failed to retrieve app configuration');
            }
        } catch (error) {
            console.error('❌ Failed to get app config:', error);
            throw error;
        }
    }

    // Clear all cached configurations
    clearCache(): void {
        console.log('[SecureConfigService] Clearing all cached configurations');
        this.cachedConfigs.clear();
    }

    // Clear cache for specific environment
    clearCacheForEnvironment(environment: 'dev' | 'prod'): void {
        const azureKey = `azure_${environment}`;
        const allKey = `all_${environment}`;
        console.log(`[SecureConfigService] Clearing cache for environment: ${environment}`);
        this.cachedConfigs.delete(azureKey);
        this.cachedConfigs.delete(allKey);
    }

    // Get all configurations securely
    async getAllConfigs(environment?: 'dev' | 'prod'): Promise<{ azure: AzureADConfig; app: AppConfig }> {
        const detectedEnvironment = environment || getEnvironment();
        const cacheKey = `all_${detectedEnvironment}`;

        if (this.cachedConfigs.has(cacheKey)) {
            return this.cachedConfigs.get(cacheKey);
        }

        try {
            const getSecureConfig = httpsCallable(this.functions, 'getSecureConfig');
            const result = await getSecureConfig({
                configType: 'all',
                environment: detectedEnvironment,
                hostname: window.location.hostname
            });

            const response = result.data as SecureConfigResponse;

            if (response.success && response.config.azure && response.config.app) {
                const configs = {
                    azure: response.config.azure,
                    app: response.config.app
                };
                this.cachedConfigs.set(cacheKey, configs);
                return configs;
            } else {
                throw new Error('Failed to retrieve configurations');
            }
        } catch (error) {
            console.error('❌ Failed to get all configs:', error);
            throw error;
        }
    }

    // Validate Azure AD configuration
    async validateAzureConfig(clientId: string, authority: string, redirectUri: string): Promise<boolean> {
        try {
            const validateAzureConfig = httpsCallable(this.functions, 'validateAzureConfig');
            const result = await validateAzureConfig({ clientId, authority, redirectUri });

            const response = result.data as any;
            return response.success;
        } catch (error) {
            console.error('❌ Azure AD config validation failed:', error);
            return false;
        }
    }

    // Get environment-specific configuration
    async getEnvironmentConfig(environment: 'dev' | 'prod'): Promise<any> {
        try {
            const getEnvironmentConfig = httpsCallable(this.functions, 'getEnvironmentConfig');
            const result = await getEnvironmentConfig({ environment });

            const response = result.data as any;

            if (response.success) {
                return response.config;
            } else {
                throw new Error('Failed to retrieve environment configuration');
            }
        } catch (error) {
            console.error('❌ Failed to get environment config:', error);
            throw error;
        }
    }



    // Get cache status
    getCacheStatus(): { size: number; keys: string[] } {
        return {
            size: this.cachedConfigs.size,
            keys: Array.from(this.cachedConfigs.keys())
        };
    }
}

// Export singleton instance
export const secureConfigService = SecureConfigService.getInstance();
